import { forwardRef, useId } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Header checkbox in a table with a partial selection. */
  indeterminate?: boolean;
  containerClassName?: string;
}

/**
 * A real <input type="checkbox">, visually hidden but still the focus target and
 * still keyboard/form/AT native. The painted box is a sibling driven by :checked.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, error, indeterminate, className, containerClassName, id, disabled, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      <div className="flex items-start gap-2">
        <span className="relative flex h-5 items-center">
          <input
            ref={(node) => {
              if (node) node.indeterminate = Boolean(indeterminate);
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            id={inputId}
            type="checkbox"
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            className={cn('peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-[4px]', className)}
            {...props}
          />
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute left-0 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-[4px]',
              // The tick inherits `currentColor`, so switching the wrapper's text
              // colour is what reveals it — peer-* variants only reach siblings.
              'border border-strong bg-surface text-transparent transition-colors',
              'peer-checked:border-accent peer-checked:bg-accent peer-checked:text-fg-onAccent',
              'peer-indeterminate:border-accent peer-indeterminate:bg-accent peer-indeterminate:text-fg-onAccent',
              'peer-disabled:opacity-50',
              error && 'border-danger',
            )}
          >
            {indeterminate ? (
              <Minus className="h-3 w-3" strokeWidth={3} />
            ) : (
              <Check className="h-3 w-3" strokeWidth={3} />
            )}
          </span>
        </span>
        {label ? (
          <label
            htmlFor={inputId}
            className={cn(
              'cursor-pointer select-none text-sm leading-5 text-fg',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {label}
          </label>
        ) : null}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="pl-6 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="pl-6 text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
